import {AxiosInstance, AxiosRequestConfig} from 'axios';
import rateLimit from 'axios-rate-limit';
import {IRenpyUploadResponse} from "./IRenpyUploadResponse";

/**
 * Service for interacting with the RenPy API.
 * 
 * This service handles operations related to RenPy game management,
 * including uploading RenPy game builds to S3/CloudFront.
 * 
 * The service uses axios with rate limiting to prevent overwhelming
 * the API with too many concurrent requests.
 */
export class RenpyApiService {
  private readonly _rateLimitInstance: AxiosInstance;
  private readonly _instance: AxiosInstance;

  public readonly MAX_REQUESTS_COUNT = 3;

  /**
   * Creates a new RenpyApiService instance.
   * 
   * @param service - Axios instance configured for the RenPy API endpoint
   */
  constructor(service: AxiosInstance) {
    this._rateLimitInstance = rateLimit(service, { maxRequests: this.MAX_REQUESTS_COUNT });
    this._instance = service;
  }

  /**
   * Uploads a RenPy game zip file for a specific book and episode.
   * 
   * The uploaded zip file should contain the compiled RenPy game with:
   * - index.html (entry point)
   * - renpy.wasm (WebAssembly runtime)
   * - All game assets and scripts
   * 
   * @param bookUuid - Unique identifier for the book
   * @param episodeUuid - Unique identifier for the episode
   * @param revision - Revision number or identifier
   * @param formData - Form data containing the game file (field name: 'gameFile')
   * @param config - Optional Axios request configuration (e.g., for upload progress tracking)
   * @returns Promise resolving to upload response with CDN URL and metadata
   * 
   * @throws {Error} When upload fails due to validation, network, or server errors
   * 
   * @example
   * ```typescript
   * const formData = new FormData();
   * formData.append('gameFile', zipFile);
   * 
   * const config = {
   *   onUploadProgress: (progressEvent) => {
   *     const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
   *     console.log(`Upload progress: ${percentCompleted}%`);
   *   },
   * };
   * 
   * const response = await renpyApiService.uploadRenpyGame(
   *   'book-uuid-123',
   *   'episode-uuid-456',
   *   '1',
   *   formData,
   *   config
   * );
   * 
   * if (response.success) {
   *   console.log('Game available at:', response.indexUrl);
   * }
   * ```
   */
  public async uploadRenpyGame(
    bookUuid: string,
    episodeUuid: string,
    revision: string,
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<IRenpyUploadResponse> {
    const response = await this._instance.put(
      `/programs/upload/${bookUuid}/${episodeUuid}/${revision}`,
      formData,
      config
    );
    return response.data;
  }
}

